/**
 * Skills Add Command
 * Install a skill from Jai1 server or npm skills registry
 */
import { Command } from 'commander';
import { join } from 'path';
import chalk from 'chalk';
import { confirm, checkbox } from '@inquirer/prompts';
import { ConfigService } from '../../services/config.service.js';
import { SkillsService } from '../../services/skills.service.js';
import { ValidationError } from '../../errors/index.js';
import { checkboxTheme } from '../../utils/prompt-theme.js';

export function createSkillsAddCommand(): Command {
    return new Command('add')
        .description('Install a skill to .jai1/skills/')
        .argument('<name>', 'Skill name or source (npm: GitHub shorthand, URL)')
        .option('--skillsh', 'Install from npm skills registry instead of Jai1 server')
        .option('--sync', 'Auto-sync to IDE(s) after install')
        .option('--ides <ides...>', 'Target IDEs for sync (cursor, windsurf, antigravity, claude, opencode, codex)')
        .option('--all', 'Sync to all available IDEs')
        .option('-y, --yes', 'Headless mode (skip all prompts)')
        .action(async (name: string, options: {
            skillsh?: boolean;
            sync?: boolean;
            ides?: string[];
            all?: boolean;
            yes?: boolean;
        }) => {
            const skillsService = new SkillsService();
            const projectRoot = process.cwd();
            const headless = options.yes === true;
            let installedSlugs: string[] = [];

            if (options.skillsh) {
                // Install from npm skills registry
                console.log(chalk.cyan(`🌐 Đang cài đặt skill từ npm: ${name}...`));
                console.log();

                const result = await skillsService.npmSkillsAdd(name, projectRoot);
                console.log(result.output);
                console.log(chalk.green('✅ Cài đặt từ npm thành công!'));
                installedSlugs = result.installedSlugs;
            } else {
                // Install from Jai1 server
                const configService = new ConfigService();
                const config = await configService.load();

                if (!config) {
                    throw new ValidationError('Chưa xác thực. Chạy "jai1 auth" trước.');
                }

                console.log(chalk.cyan(`📦 Đang cài đặt skill: ${name}...`));
                console.log();

                const targetDir = join(projectRoot, '.jai1');
                await skillsService.installFromServer(config, name, targetDir);
                installedSlugs = [name.replace(/^skills\//, '')];

                console.log(chalk.green(`✅ Đã cài đặt skill "${name}" vào .jai1/skills/${name}/`));
            }

            console.log();

            // Auto-sync if requested
            if (options.sync) {
                let selectedIdes: string[];

                // IDE alias map for backward compatibility
                const ideAliases: Record<string, string> = {
                    'claudecode': 'claude',
                    'codexcli': 'codex',
                    'openai-codex': 'codex',
                };
                const normalizeIde = (ide: string): string => ideAliases[ide] ?? ide;

                if (options.all) {
                    selectedIdes = skillsService.getIDETargets().map(t => t.id);
                } else if (options.ides && options.ides.length > 0) {
                    selectedIdes = options.ides.map(normalizeIde);
                } else if (headless) {
                    throw new Error(
                        'Headless mode (-y) với --sync yêu cầu --ides hoặc --all.\n' +
                        'Ví dụ:\n' +
                        '  j skills add brainstorming --sync --ides cursor windsurf -y\n' +
                        '  j skills add brainstorming --sync --all -y'
                    );
                } else {
                    const targets = skillsService.getIDETargets();
                    selectedIdes = await checkbox({
                        message: 'Sync sang IDE(s) nào?',
                        choices: targets.map(t => ({
                            name: `${t.icon} ${t.name}`,
                            value: t.id,
                        })),
                        theme: checkboxTheme,
                    });
                }

                if (selectedIdes.length > 0) {
                    console.log(chalk.cyan('🔄 Đang sync sang IDE(s)...'));
                    console.log();

                    // Extract skill slug from name
                    const slugs = installedSlugs.length > 0
                        ? installedSlugs
                        : [name.includes('/') ? name.split('/').pop()!.split('@').pop()! : name];

                    const result = await skillsService.syncToIdes(
                        projectRoot,
                        selectedIdes,
                        slugs,
                        (res) => {
                            const icon = res.status === 'created' ? '✓' :
                                res.status === 'updated' ? '↻' : '✗';
                            console.log(`   ${icon} ${res.ide}: ${res.skill} → ${res.path}`);
                        }
                    );

                    console.log();
                    console.log(chalk.green(`✅ Sync hoàn tất! Created: ${result.created}, Updated: ${result.updated}`));
                    if (result.errors > 0) {
                        console.log(chalk.yellow(`⚠️  Errors: ${result.errors}`));
                    }
                }
            } else {
                // Print guidance
                console.log(chalk.dim('💡 Chạy "j skills sync" để đồng bộ sang IDE(s)'));
                console.log(chalk.dim('   hoặc "j ide sync" để sync toàn bộ .jai1/'));
            }
        });
}
